/**
 * Pac-Man renderer (C). Draws an engine state onto a 224×248 2D context.
 * Walls are reduced to a static rect list once at module load (immutable);
 * everything else is drawn from state each frame. No text — HUD is DOM.
 */
import {
    MAZE, COLS, ROWS, TILE, WIDTH, HEIGHT, DYING_MS, mazeChar, DIRS,
} from './pacmanEngine';

export const COLORS = {
    background: '#000000',
    wall: '#1a1aa6',
    wallEdge: '#4b4bff',
    door: '#ffb8de',
    pellet: '#ffb8ae',
    pacman: '#ffff00',
    frightened: '#2121ff',
    frightenedFlash: '#ffffff',
    eye: '#ffffff',
    pupil: '#2121ff',
    ghosts: {
        blinky: '#ff0000', pinky: '#ffb8ff', inky: '#00ffff', clyde: '#ffb852',
    },
};

const DIR_ANGLE = {
    right: 0, down: Math.PI / 2, left: Math.PI, up: Math.PI * 1.5,
};

function buildWallRects() {
    const rects = [];
    for (let row = 0; row < ROWS; row += 1) {
        for (let col = 0; col < COLS; col += 1) {
            if (MAZE[row][col] !== '#') {
                continue;
            }
            const x = col * TILE;
            const y = row * TILE;
            rects.push({
                x, y, w: TILE, h: TILE, style: 'wall',
            });
            const open = (c, r) => {
                const ch = mazeChar(c, r);
                return ch !== '#' && ch !== 'x';
            };
            if (open(col, row - 1)) {
                rects.push({
                    x, y, w: TILE, h: 1, style: 'edge',
                });
            }
            if (open(col, row + 1)) {
                rects.push({
                    x, y: y + TILE - 1, w: TILE, h: 1, style: 'edge',
                });
            }
            if (open(col - 1, row)) {
                rects.push({
                    x, y, w: 1, h: TILE, style: 'edge',
                });
            }
            if (open(col + 1, row)) {
                rects.push({
                    x: x + TILE - 1, y, w: 1, h: TILE, style: 'edge',
                });
            }
        }
    }
    return rects;
}

// Derived once; never mutated.
export const WALL_RECTS = buildWallRects();

function drawWalls(ctx) {
    for (let i = 0; i < WALL_RECTS.length; i += 1) {
        const r = WALL_RECTS[i];
        ctx.fillStyle = r.style === 'wall' ? COLORS.wall : COLORS.wallEdge;
        ctx.fillRect(r.x, r.y, r.w, r.h);
    }
    ctx.fillStyle = COLORS.door;
    ctx.fillRect(13 * TILE, 12 * TILE + 3, 2 * TILE, 2);
}

function drawDots(ctx, state, frame) {
    ctx.fillStyle = COLORS.pellet;
    const showPower = frame % 32 < 16;
    for (let row = 0; row < ROWS; row += 1) {
        for (let col = 0; col < COLS; col += 1) {
            const dot = state.dots[row * COLS + col];
            if (dot === 1) {
                ctx.fillRect(col * TILE + 3, row * TILE + 3, 2, 2);
            } else if (dot === 2 && showPower) {
                ctx.fillRect(col * TILE + 1, row * TILE + 1, 6, 6);
            }
        }
    }
}

function drawPacman(ctx, state) {
    const p = state.pacman;
    const angle = DIR_ANGLE[p.dir] || 0;
    let mouth;
    if (state.status === 'dying') {
        // opens all the way as the death pause runs out
        mouth = Math.PI * Math.min(1, Math.max(0, 1 - state.statusMs / DYING_MS));
    } else {
        mouth = Math.abs(Math.sin(p.mouth)) * 0.35 * Math.PI;
    }
    ctx.fillStyle = COLORS.pacman;
    ctx.beginPath();
    ctx.arc(p.x, p.y, 7, angle + mouth, angle + Math.PI * 2 - mouth);
    ctx.lineTo(p.x, p.y);
    ctx.closePath();
    ctx.fill();
}

function drawEyes(ctx, g) {
    const d = DIRS[g.dir] || DIRS.left;
    ctx.fillStyle = COLORS.eye;
    ctx.fillRect(g.x - 5, g.y - 4, 3, 4);
    ctx.fillRect(g.x + 2, g.y - 4, 3, 4);
    ctx.fillStyle = COLORS.pupil;
    ctx.fillRect(g.x - 4 + d.dx, g.y - 3 + d.dy, 2, 2);
    ctx.fillRect(g.x + 3 + d.dx, g.y - 3 + d.dy, 2, 2);
}

function drawGhost(ctx, g, state, frame) {
    if (g.mode === 'eyes' || g.mode === 'entering') {
        drawEyes(ctx, g);
        return;
    }
    let body = COLORS.ghosts[g.name] || COLORS.ghosts.blinky;
    if (g.mode === 'frightened') {
        body = state.frightFlash && frame % 8 < 4 ? COLORS.frightenedFlash : COLORS.frightened;
    }
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.moveTo(g.x - 7, g.y + 7);
    ctx.lineTo(g.x - 7, g.y - 1);
    ctx.arc(g.x, g.y - 1, 7, Math.PI, 0);
    ctx.lineTo(g.x + 7, g.y + 7);
    ctx.lineTo(g.x + 4.7, g.y + 4);
    ctx.lineTo(g.x + 2.3, g.y + 7);
    ctx.lineTo(g.x, g.y + 4);
    ctx.lineTo(g.x - 2.3, g.y + 7);
    ctx.lineTo(g.x - 4.7, g.y + 4);
    ctx.closePath();
    ctx.fill();
    if (g.mode === 'frightened') {
        ctx.fillStyle = COLORS.eye;
        ctx.fillRect(g.x - 4, g.y - 3, 2, 2);
        ctx.fillRect(g.x + 2, g.y - 3, 2, 2);
        ctx.fillRect(g.x - 5, g.y + 2, 2, 1);
        ctx.fillRect(g.x - 2, g.y + 1, 4, 1);
        ctx.fillRect(g.x + 3, g.y + 2, 2, 1);
        return;
    }
    drawEyes(ctx, g);
}

export function render(ctx, state, frame = 0) {
    ctx.fillStyle = COLORS.background;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);
    drawWalls(ctx);
    drawDots(ctx, state, frame);
    if (state.status !== 'dying') {
        state.ghosts.forEach(g => drawGhost(ctx, g, state, frame));
    }
    drawPacman(ctx, state);
}
